import { injectable, inject } from 'inversify';
import { Identity } from 'openfin/_v2/main';

import { Inject } from '../common/Injectables';
import { APITopic, Events, SERVICE_IDENTITY } from '../../client/internal';
import { Transport, Targeted } from '../../client/EventRouter';
import type {
  NotificationActionEvent,
  NotificationFormSubmittedEvent,
  NotificationsCountChanged,
} from '../../client';

import { APIHandler } from './APIHandler';
import { ClientRegistry } from './ClientRegistry';

type DeferrableEvent = Targeted<
  Transport<NotificationActionEvent | NotificationFormSubmittedEvent>
>;
interface DeferredEvent {
  targetUuid: string;
  event: DeferrableEvent;
}

/**
 * Notification event message handler. EventPump internally handles the events added
 * by either dispatching them immediately or deferring the dispatch.
 */
@injectable()
export class EventPump {
  private readonly _apiHandler: APIHandler<APITopic, Events>;
  private readonly _clientRegistry: ClientRegistry;
  private _deferredEvents: DeferredEvent[] = [];

  constructor(
    @inject(Inject.CLIENT_REGISTRY) clientRegistry: ClientRegistry,
    @inject(Inject.API_HANDLER) apiHandler: APIHandler<APITopic, Events>
  ) {
    this._apiHandler = apiHandler;
    this._clientRegistry = clientRegistry;
    this._clientRegistry.onAppActionReady.add(this.dispatchDeferredEvents, this);
  }

  /**
   * Adds an event to EventPump to be dispatched to the specified client.
   * Type args:
   *   T: Defines the type of event which occured and needs to be passed to the client.
   *
   * @param targetUuid Uuid of target client.
   * @param event Notification event to be dispatched
   */
  public async push<T extends Events>(
    targetUuid: string,
    event: Targeted<Transport<T>>
  ): Promise<void> {
    if (targetUuid === SERVICE_IDENTITY.uuid) {
      // Cannot send events to the provider
      return;
    }
    if (event.type === 'notification-action' || event.type === 'notification-form-submitted') {
      // Send the event straight away
      if (this._clientRegistry.isAppActionReady(targetUuid)) {
        if (this._apiHandler.channel) {
          const connectedAppWindows = this._apiHandler
            .getClientConnections()
            .filter((client: Identity) => client.uuid === targetUuid);

          connectedAppWindows.forEach((window) => {
            this._apiHandler.dispatchEvent(window, event);
          });
        }
      } else {
        // If the app is not running and the event is an action/form defer the event
        // until the app is relaunched.
        this._deferredEvents.push({ targetUuid, event: (event as unknown) as DeferrableEvent });
        await this._clientRegistry.tryLaunchApplication(targetUuid);
      }
    } else {
      // We may get called as soon as all injected components are initialized,
      // which may be before APIHandler is initialized. See [SERVICE-729]
      // eslint-disable-next-line no-lonely-if
      if (this._apiHandler.channel) {
        const connectedAppWindows = this._apiHandler
          .getClientConnections()
          .filter((client: Identity) => client.uuid === targetUuid);

        connectedAppWindows.forEach((window) => {
          this._apiHandler.dispatchEvent(window, event);
        });
      }
    }
  }

  /**
   * Broadcast an event to the `EventPump` to be dispatched to all clients listening.
   */
  public async broadcastCountEvent<T extends NotificationsCountChanged>(
    event: Targeted<Transport<T>>
  ): Promise<void> {
    this._clientRegistry.NotificationCountListeningWindows.forEach((id) => {
      this._apiHandler.dispatchEvent(id, event);
    });
  }

  /**
   * Dispatches relevant deferred events to a client once a handshake occurs between the provider and the client.
   *
   * @param app Identity of target client.
   */
  private dispatchDeferredEvents(app: Identity): void {
    this._deferredEvents = this._deferredEvents.filter((event) => {
      const shouldDispatch: boolean = event.targetUuid === app.uuid;
      if (shouldDispatch) {
        this._apiHandler.dispatchEvent(app, event.event);
      }
      return !shouldDispatch;
    });
  }
}
